import { Box, Card, CardContent, Grid, Stack, Typography } from '@mui/material';
import GroupsIcon from '@mui/icons-material/Groups';
import EventIcon from '@mui/icons-material/Event';
import RateReviewIcon from '@mui/icons-material/RateReview';
import VerifiedIcon from '@mui/icons-material/Verified';
import PaymentsIcon from '@mui/icons-material/Payments';

import { PageHeader } from '@/components/PageHeader';
import { CenteredLoader } from '@/components/CenteredLoader';
import { useApiQuery } from '@/hooks/useApiQuery';
import { practice } from '@/api/endpoints';

/**
 * Practice overview.
 *
 * Aggregate dashboard composed from a few parallel fetches. Shows:
 *   - clinician count + how many are pending verification
 *   - today's appointment count + open follow-ups
 *   - reviews awaiting moderation
 *   - active care packages and memberships
 *
 * Counts are derived from list endpoints — Tour 4 ships the dashboard
 * with these aggregates; a dedicated `/api/medical/practice/dashboard/`
 * summary endpoint can replace this in a follow-up tour.
 */

type ClinicianRow = { id: number; verification_status?: string };
type AppointmentRow = { id: string; status: string; start_at: string };
type ReviewRow = { id: number; moderation_status: string };
type PackageRow = { id: number; is_active: boolean };
type MembershipPlanRow = { id: number; is_active: boolean };

export function PracticeOverviewPage() {
  const cliniciansQ = useApiQuery({
    queryKey: ['practice', 'clinicians'],
    queryFn:  practice.clinicians.list as () => Promise<ClinicianRow[]>,
  });
  const apptsQ = useApiQuery({
    queryKey: ['practice', 'appointments'],
    queryFn:  () => practice.appointments.list() as Promise<AppointmentRow[]>,
  });
  const reviewsQ = useApiQuery({
    queryKey: ['practice', 'reviews'],
    queryFn:  practice.reviews.list as () => Promise<ReviewRow[]>,
  });
  const packagesQ = useApiQuery({
    queryKey: ['practice', 'care-packages'],
    queryFn:  practice.carePackages.list as () => Promise<PackageRow[]>,
  });
  const plansQ = useApiQuery({
    queryKey: ['practice', 'membership-plans'],
    queryFn:  practice.membershipPlans.list as () => Promise<MembershipPlanRow[]>,
  });

  if (cliniciansQ.isPending && apptsQ.isPending && reviewsQ.isPending) {
    return <CenteredLoader label="Loading practice overview…" />;
  }

  const clinicians = cliniciansQ.data ?? [];
  const appointments = apptsQ.data ?? [];
  const reviews = reviewsQ.data ?? [];
  const packages = packagesQ.data ?? [];
  const plans = plansQ.data ?? [];

  const today = new Date(); today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
  const todayCount = appointments.filter((a) => {
    const s = new Date(a.start_at);
    return s >= today && s < tomorrow;
  }).length;

  const pendingVerification = clinicians.filter(
    (c) => c.verification_status === 'pending' || c.verification_status === 'in_review',
  ).length;
  const reviewsToModerate = reviews.filter((r) => r.moderation_status === 'pending').length;
  const activePackages = packages.filter((p) => p.is_active).length;
  const activePlans = plans.filter((p) => p.is_active).length;

  return (
    <>
      <PageHeader
        title="Practice overview"
        subtitle="Today's load, pending verifications, and revenue snapshot."
      />

      <Grid container spacing={2}>
        <StatCard label="Clinicians"
                  primary={String(clinicians.length)}
                  secondary={pendingVerification
                    ? `${pendingVerification} pending verification`
                    : 'all verified'}
                  icon={<GroupsIcon />}
                  warn={pendingVerification > 0} />
        <StatCard label="Today's appointments"
                  primary={String(todayCount)}
                  secondary="across all clinicians"
                  icon={<EventIcon />} />
        <StatCard label="Reviews to moderate"
                  primary={String(reviewsToModerate)}
                  secondary={reviewsToModerate ? 'awaiting your decision' : 'all caught up'}
                  icon={<RateReviewIcon />}
                  warn={reviewsToModerate > 0} />
        <StatCard label="Active care packages"
                  primary={String(activePackages)}
                  secondary={`${packages.length - activePackages} retired`}
                  icon={<PaymentsIcon />} />
        <StatCard label="Active membership plans"
                  primary={String(activePlans)}
                  secondary={`${plans.length - activePlans} retired`}
                  icon={<VerifiedIcon />} />
      </Grid>
    </>
  );
}

function StatCard({
  label, primary, secondary, icon, warn,
}: {
  label: string;
  primary: string;
  secondary: string;
  icon: React.ReactNode;
  warn?: boolean;
}) {
  return (
    <Grid item xs={12} sm={6} md={4} lg={2.4}>
      <Card sx={{ borderColor: warn ? 'warning.main' : 'divider' }}>
        <CardContent>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{
              width: 40, height: 40, borderRadius: 1.5,
              bgcolor: 'background.subtle',
              color: warn ? 'warning.main' : 'primary.main',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              {icon}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="overline" color="text.secondary"
                          sx={{ display: 'block', lineHeight: 1.2 }}>
                {label}
              </Typography>
              <Typography variant="h5" component="div" className="tabular">
                {primary}
              </Typography>
              <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block' }}>
                {secondary}
              </Typography>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Grid>
  );
}
